import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { TrendingUp, Truck, PiggyBank, CalendarDays, ChevronRight } from "lucide-react";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";

const MONTH_SHORT = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];
const MONTH_LONG = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
const chf = (v) => `CHF ${Number(v || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const YearKpi = ({ label, value, color, testid }) => (
  <div className="card p-6" style={{ borderColor: "rgba(212,175,55,0.35)" }} data-testid={testid}>
    <div className="label-eyebrow">{label}</div>
    <div className="mt-3 text-3xl md:text-4xl font-mono font-semibold tracking-tight" style={{ color }} data-testid={`${testid}-value`}>
      {value}
    </div>
  </div>
);

const MonthKpi = ({ label, value, icon: Icon, color, testid }) => (
  <div className="card p-4" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div className="label-eyebrow">{label}</div>
      <Icon size={14} strokeWidth={1.5} style={{ color }} />
    </div>
    <div className="mt-2 text-xl font-mono font-semibold tracking-tight" style={{ color }} data-testid={`${testid}-value`}>
      {value}
    </div>
  </div>
);

export const ProfitOverview = () => {
  const [data, setData] = useState(null);
  const [monthIdx, setMonthIdx] = useState(new Date().getMonth());

  useEffect(() => {
    api.get("/stats/profit").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return null;

  const sel = data.months[monthIdx] || {};
  const chartData = data.months.map((m, i) => ({
    name: MONTH_SHORT[i],
    Umsatz: m.revenue,
    Kosten: m.costs,
    Gewinn: m.profit,
  }));

  const onChartClick = (e) => {
    if (e && e.activeLabel) {
      const i = MONTH_SHORT.indexOf(e.activeLabel);
      if (i >= 0) setMonthIdx(i);
    }
  };

  return (
    <div className="mt-10" data-testid="profit-overview">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-xl font-semibold">Umsatz &amp; Gewinn {data.year}</h2>
        <div className="text-[11px] uppercase tracking-wider text-[var(--text-3)]">
          Basis: zugestellte Aufträge · Einnahmen (Kunden) − Ausgaben (Fahrer)
        </div>
      </div>

      {/* === JAHR (prominent) === */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="profit-year-kpis">
        <YearKpi label={`Jahresumsatz ${data.year}`} value={chf(data.totals.revenue)} color="var(--header)" testid="profit-year-revenue" />
        <YearKpi label={`Fahrerkosten ${data.year}`} value={chf(data.totals.costs)} color="#9A3412" testid="profit-year-costs" />
        <YearKpi
          label={`Gewinn ${data.year}`}
          value={chf(data.totals.profit)}
          color={data.totals.profit >= 0 ? "#15803D" : "#B91C1C"}
          testid="profit-year-profit"
        />
      </div>

      {/* === MONAT (separat wählbar) === */}
      <div className="card p-5 mt-4" data-testid="profit-month-section">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2">
            <CalendarDays size={16} strokeWidth={1.5} style={{ color: "#D4AF37" }} />
            <span className="text-sm font-semibold">Monatsansicht</span>
          </div>
          <select
            data-testid="profit-month-select"
            className="input-base text-sm w-auto"
            value={monthIdx}
            onChange={(e) => setMonthIdx(Number(e.target.value))}
          >
            {MONTH_LONG.map((m, i) => (
              <option key={m} value={i}>{m} {data.year}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="profit-month-kpis">
          <MonthKpi label="Umsatz" value={chf(sel.revenue)} icon={TrendingUp} color="var(--header)" testid="profit-kpi-revenue" />
          <MonthKpi label="Fahrerkosten" value={chf(sel.costs)} icon={Truck} color="#9A3412" testid="profit-kpi-costs" />
          <MonthKpi
            label="Gewinn"
            value={chf(sel.profit)}
            icon={PiggyBank}
            color={(sel.profit || 0) >= 0 ? "#15803D" : "#B91C1C"}
            testid="profit-kpi-profit"
          />
        </div>
        <div className="text-[11px] text-[var(--text-3)] mt-3">
          {sel.orders || 0} zugestellte Aufträge im {MONTH_LONG[monthIdx]} · Tipp: Monat auch per Klick auf einen Balken im Diagramm wählbar
        </div>
      </div>

      {/* === Jahresverlauf === */}
      <div className="card p-5 mt-4" data-testid="profit-year-chart">
        <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer>
            <ComposedChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 0 }} onClick={onChartClick} style={{ cursor: "pointer" }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.07)" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#71717A" }} axisLine={false} tickLine={false} />
              <YAxis
                tick={{ fontSize: 11, fill: "#71717A" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)}
                width={42}
              />
              <Tooltip
                formatter={(value, name) => [chf(value), name]}
                contentStyle={{ borderRadius: 4, border: "1px solid rgba(0,0,0,0.1)", fontSize: 12 }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Umsatz" fill="rgba(30,86,49,0.75)" radius={[3, 3, 0, 0]} maxBarSize={28} />
              <Bar dataKey="Kosten" fill="rgba(234,88,12,0.55)" radius={[3, 3, 0, 0]} maxBarSize={28} />
              <Line type="monotone" dataKey="Gewinn" stroke="#D4AF37" strokeWidth={2.5} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* === Alle Monate als Tabelle === */}
      <details className="card mt-4 px-5 py-4" data-testid="profit-month-table">
        <summary className="cursor-pointer flex items-center justify-between text-sm font-semibold text-[var(--text)]">
          <span>Alle Monate im Detail</span>
          <ChevronRight size={16} className="text-[var(--text-3)]" />
        </summary>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead>
              <tr className="border-b border-[var(--border)] text-[var(--text-3)] uppercase text-[11px] tracking-wider">
                <th className="text-left py-2 px-3">Monat</th>
                <th className="text-right py-2 px-3">Aufträge</th>
                <th className="text-right py-2 px-3">Umsatz</th>
                <th className="text-right py-2 px-3">Fahrerkosten</th>
                <th className="text-right py-2 px-3">Gewinn</th>
              </tr>
            </thead>
            <tbody>
              {data.months.map((m, i) => (
                <tr
                  key={m.month}
                  className={`border-b border-[var(--border)] hover:bg-[var(--surface-2)] cursor-pointer ${i === monthIdx ? "bg-[#D4AF37]/[0.06]" : ""}`}
                  onClick={() => setMonthIdx(i)}
                  data-testid={`profit-table-row-${i}`}
                >
                  <td className="py-2 px-3 text-[var(--text)]">{MONTH_LONG[i]}</td>
                  <td className="py-2 px-3 text-right font-mono text-[var(--text-2)]">{m.orders}</td>
                  <td className="py-2 px-3 text-right font-mono text-[var(--text)]">{chf(m.revenue)}</td>
                  <td className="py-2 px-3 text-right font-mono text-[var(--text-2)]">{chf(m.costs)}</td>
                  <td className="py-2 px-3 text-right font-mono font-semibold" style={{ color: m.profit >= 0 ? "#15803D" : "#B91C1C" }}>
                    {chf(m.profit)}
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 border-[var(--border-strong)]">
                <td className="py-2.5 px-3 font-semibold text-[var(--text)]">Total {data.year}</td>
                <td className="py-2.5 px-3 text-right font-mono font-semibold text-[var(--text)]">{data.totals.orders}</td>
                <td className="py-2.5 px-3 text-right font-mono font-semibold text-[var(--text)]">{chf(data.totals.revenue)}</td>
                <td className="py-2.5 px-3 text-right font-mono font-semibold text-[var(--text)]">{chf(data.totals.costs)}</td>
                <td className="py-2.5 px-3 text-right font-mono font-semibold" style={{ color: data.totals.profit >= 0 ? "#15803D" : "#B91C1C" }}>
                  {chf(data.totals.profit)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
};
